import { useTranslation } from '../../../shared/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Card, Table, Tag, Button, Space, Input, Typography, Popconfirm, message, Segmented, Empty, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, SafetyCertificateOutlined, ReloadOutlined, GlobalOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import webmailService from '../../services/webmailService';
import DnsOneClick from '../../components/DnsOneClick';
import BimiCard from './BimiCard';

const { Text } = Typography;
const ok = (r) => r && (r.code === 0 || r.success);
/**
 * 域名设置：别名域 + 域级发件人名单（2026-10-07）
 */
const DomainSettingsDrawer = ({ open, domain, onClose }) => {
    const { t } = useTranslation();
    const [width, setWidth] = useState(window.innerWidth);
    const [catchAll, setCatchAll] = useState(null);
    const [mailboxes, setMailboxes] = useState([]);
    const [ad, setAd] = useState(null);
    const [lists, setLists] = useState({ allow: [], block: [] });
    const [newAlias, setNewAlias] = useState('');
    const [newSender, setNewSender] = useState('');
    const [listType, setListType] = useState('block');
    const [busy, setBusy] = useState('');

    useEffect(() => {
        const f = () => setWidth(window.innerWidth);
        window.addEventListener('resize', f);
        return () => window.removeEventListener('resize', f);
    }, []);

    const id = domain?.local_id;
    const load = useCallback(async () => {
        if (!id) return;
        const [a, l, ca, us] = await Promise.all([
            webmailService.getAliasDomains(id), webmailService.getSenderLists(id),
            webmailService.getCatchAll(id), webmailService.getUsers({ domain_id: id }),
        ]);
        if (ok(ca)) setCatchAll(ca.data); else setCatchAll(null);
        const ulist = Array.isArray(us?.data) ? us.data : (Array.isArray(us?.data?.items) ? us.data.items : []);
        setMailboxes(ulist.filter(u => u.email));
        if (ok(a)) setAd(a.data); else message.error(a?.error || t('Failed to load alias domains'));
        if (ok(l)) setLists(l.data || { allow: [], block: [] }); else message.error(l?.error || t('Failed to load list'));
    }, [id, t]);

    useEffect(() => { if (open) load(); else { setAd(null); setNewAlias(''); setNewSender(''); } }, [open, load]);

    const run = async (key, fn, okMsg) => {
        setBusy(key);
        const r = await fn();
        setBusy('');
        if (ok(r)) { if (okMsg !== false) message.success(okMsg || r.message || t('Done')); return r; }
        message.error(r?.error || r?.message || t('Operation failed'));
        return null;
    };

    const addAlias = async () => {
        if (!newAlias.trim()) return;
        const r = await run('add', () => webmailService.addAliasDomain(id, newAlias.trim()), t('Added. Please add DNS records, then verify.'));
        if (r) { setNewAlias(''); setAd(r.data); }
    };
    const verify = async (it) => {
        setBusy('v' + it.id);
        const r = await webmailService.verifyAliasDomain(id, it.id);
        setBusy('');
        if (!ok(r)) return message.error(r?.error || t('Verification failed'));
        const d = r.data || {};
        if (d.verified) message.success(t("{{domain}} verified successfully", { domain: it.domain }));
        else message.warning(t("Failed: {{mx}}{{separator}}{{txt}}", { mx: !d.mx ? t('MX does not point to the mail server') : '', separator: !d.mx && !d.txt ? '；' : '', txt: !d.txt ? t('Verification TXT record not found') : '' }));
        load();
    };
    const setCatchAllDest = async (dest) => {
        const r = await run('ca', () => webmailService.setCatchAll(id, dest), dest ? t('Catch-all turned on') : t('Catch-all turned off'));
        if (r) setCatchAll(r.data);
    };
    const delAlias = async (it) => { if (await run('x' + it.id, () => webmailService.deleteAliasDomain(id, it.id), t('Removed'))) load(); };

    const addSender = async () => {
        if (!newSender.trim()) return;
        const r = await run('s', () => webmailService.addSenderList(id, listType, newSender.trim()), t('Added'));
        if (r) { setNewSender(''); setLists({ allow: r.data.allow || [], block: r.data.block || [] }); }
    };
    const delSender = async (e) => {
        const r = await run('e' + e.id, () => webmailService.deleteSenderList(id, e.id), t('Removed'));
        if (r) setLists({ allow: r.data.allow || [], block: r.data.block || [] });
    };

    const isMobile = width < 640;
    const limitText = ad ? (ad.limit < 0 ? t("{{used}} / Unlimited", { used: ad.used }) : `${ad.used} / ${ad.limit}`) : '';
    const full = ad && ad.limit >= 0 && ad.used >= ad.limit;
    const senders = [
        ...lists.block.map(e => ({ ...e, list: 'block' })),
        ...lists.allow.map(e => ({ ...e, list: 'allow' })),
    ];

    return (
        <Drawer
            title={<Space><GlobalOutlined />{t('Domain settings · {{domain}}', { domain: domain?.name })}</Space>}
            open={open}
            onClose={onClose}
            width={isMobile ? '100%' : Math.min(760, width - 80)}
            extra={<Button icon={<ReloadOutlined />} onClick={load} />}
            destroyOnClose
        >
            <Card size="small" title={t("Alias domain")} extra={<Text type="secondary">{limitText}</Text>} style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    {t('Email sent to user@alias-domain is delivered to user@{{domain}}', { domain: ad?.main || domain?.name })}
                </Text>
                <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                    <Input placeholder={t("E.g. example.net")} value={newAlias} onChange={e => setNewAlias(e.target.value)} onPressEnter={addAlias} disabled={full} />
                    <Button type="primary" icon={<PlusOutlined />} loading={busy === 'add'} onClick={addAlias} disabled={full}>{t("Add")}</Button>
                </Space.Compact>
                {full && <Text type="warning" style={{ display: 'block', marginBottom: 8 }}>{ad.limit === 0 ? t('Your current plan does not include alias domains') : t('Plan limit reached')}</Text>}
                {(ad?.items || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No alias domains yet")} /> : (
                    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0, 1fr)' }}>
                        {ad.items.map(it => (
                            <div key={it.id} style={{ border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8, padding: 10, minWidth: 0, overflow: 'hidden' }}>
                                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <Space>
                                        <Text strong>{it.domain}</Text>
                                        {it.verified ? <Tag color="green" icon={<CheckCircleOutlined />}>{t("Verified")}</Tag> : <Tag color="orange">{t("Verification pending")}</Tag>}
                                    </Space>
                                    <Space size={4}>
                                        {!it.verified && <Button size="small" type="primary" icon={<SafetyCertificateOutlined />} loading={busy === 'v' + it.id} onClick={() => verify(it)}>{t("Verify")}</Button>}
                                        <Popconfirm title={t("Remove {{domain}}?", { domain: it.domain })} onConfirm={() => delAlias(it)} okText={t("Remove")} okButtonProps={{ danger: true }}>
                                            <Button size="small" danger icon={<DeleteOutlined />} loading={busy === 'x' + it.id} />
                                        </Popconfirm>
                                    </Space>
                                </Space>
                                {!it.verified && (
                                    <div style={{ marginTop: 8 }}>
                                        <DnsOneClick app="webmail_alias" id={id} aid={it.id} autoCheck={false}
                                            onApplied={() => setTimeout(() => verify(it), 3000)} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card size="small" title={t("Catch-all mailbox")} style={{ marginBottom: 16 }}
                extra={catchAll?.allowed === false ? <Tag>{t("Not in your plan")}</Tag> : null}>
                {catchAll?.allowed === false ? (
                    <Text type="secondary">{t("Deliver mail for non-existent addresses to one mailbox. Available on Business and Enterprise plans.")}</Text>
                ) : (
                    <Space.Compact style={{ width: '100%' }}>
                        <Select
                            style={{ flex: 1, minWidth: 0 }}
                            allowClear
                            placeholder={t("Off (unknown addresses bounce)")}
                            value={catchAll?.destination || undefined}
                            options={mailboxes.map(m => ({ value: m.email, label: m.email }))}
                            onChange={v => setCatchAllDest(v ?? null)}
                            loading={busy === 'ca'}
                            notFoundContent={t("Create a mailbox first")}
                        />
                    </Space.Compact>
                )}
            </Card>

            {id && <BimiCard id={id} domainName={domain?.name} />}

            <Card size="small" title={t("Sender list")} extra={<Text type="secondary">{t("Applies to the entire domain")}</Text>}>
                <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                    <Segmented
                        value={listType}
                        onChange={setListType}
                        options={[{ value: 'block', label: t('Block') }, { value: 'allow', label: t('Allow') }]}
                        style={{ marginRight: 8 }}
                    />
                    <Input placeholder={t("Email address or domain, e.g. spam@x.com / x.com")} value={newSender} onChange={e => setNewSender(e.target.value)} onPressEnter={addSender} />
                    <Button type="primary" icon={<PlusOutlined />} loading={busy === 's'} onClick={addSender}>{t("Add")}</Button>
                </Space.Compact>
                {senders.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("None yet")} /> : (
                    <Table
                        size="small" pagination={false} rowKey="id" dataSource={senders} showHeader={false}
                        columns={[
                            { width: 80, render: (_, e) => e.list === 'block' ? <Tag color="red" icon={<StopOutlined />}>{t("Block")}</Tag> : <Tag color="green" icon={<CheckCircleOutlined />}>{t("Allow")}</Tag> },
                            { dataIndex: 'value', render: (v, e) => <Space><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{e.type === 'domain' ? t('Domains') : t('Email')}</Text></Space> },
                            { width: 48, render: (_, e) => <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={busy === 'e' + e.id} onClick={() => delSender(e)} /> },
                        ]}
                    />
                )}
            </Card>
        </Drawer>
    );
};

export default DomainSettingsDrawer;
